<template>
    <main
        class="min-w-0 flex-1 border-t border-taupe-200 lg:flex overflow-y-auto bg-taupe-50"
    >
        <!-- Primary column -->
        <section
            aria-labelledby="primary-heading"
            class="flex h-full min-w-0 flex-1 flex-col lg:order-last pt-6 sm:pt-8 lg:pt-10 xl:max-w-7xl xl:mx-auto w-full"
        >
            <h1 id="primary-heading" class="sr-only">
                Admin: Projects: Translations
            </h1>
            <div class="px-4 sm:px-6 lg:px-8 pb-12 space-y-8">
                <div class="mb-6">
                    <project-header-tabs
                        current="settings"
                        :project="project.name"
                        :project-key="project.key"
                    />
                </div>

                <div
                    class="flex items-center justify-between mb-6 min-h-[2.5rem]"
                >
                    <h2 class="text-2xl font-medium text-taupe-900">
                        Project Settings
                    </h2>
                </div>

                <form @submit.prevent="save" class="space-y-8">
                    <div
                        class="rounded-xl bg-white shadow-sm border border-taupe-200 p-6 space-y-6 hover:shadow-md transition-shadow"
                    >
                        <div class="space-y-1">
                            <h3 class="text-lg font-medium text-taupe-900">
                                Project Information
                            </h3>
                            <p class="text-sm text-taupe-600">
                                Update the basic information for this project.
                            </p>
                        </div>

                        <div class="border-t border-taupe-200 pt-6 space-y-6">
                            <div class="space-y-2">
                                <label
                                    for="name"
                                    class="block text-sm font-medium text-taupe-900"
                                    >Project Name</label
                                >
                                <p class="text-sm text-taupe-600">
                                    The display name of your project.
                                </p>
                                <input
                                    type="text"
                                    v-model="data.name"
                                    name="name"
                                    id="name"
                                    autocomplete="name"
                                    placeholder="My Project"
                                    class="block w-full max-w-xl px-4 py-2.5 rounded-lg border border-taupe-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2"
                                />
                            </div>

                            <div class="space-y-2">
                                <label
                                    for="description"
                                    class="block text-sm font-medium text-taupe-900"
                                    >Description</label
                                >
                                <p class="text-sm text-taupe-600">
                                    A brief description of your project
                                    (optional).
                                </p>
                                <textarea
                                    v-model="data.description"
                                    name="description"
                                    id="description"
                                    rows="3"
                                    placeholder="Describe your project..."
                                    class="block w-full max-w-xl px-4 py-2.5 rounded-lg border border-taupe-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2"
                                ></textarea>
                            </div>
                        </div>
                    </div>

                    <div class="flex gap-3">
                        <zen-button
                            v-if="projectChanged"
                            type="button"
                            variant="secondary"
                            @click="reset"
                        >
                            Cancel
                        </zen-button>
                        <zen-button
                            v-if="can(PERMISSIONS.PROJECT_UPDATE)"
                            type="submit"
                            variant="primary"
                            :disabled="!projectChanged"
                            :processing="saving"
                        >
                            Save Changes
                        </zen-button>
                    </div>
                </form>
            </div>
        </section>
    </main>
</template>

<script>
import ProjectHeaderTabs from '@/screens/_components/admin/ProjectHeaderTabs.vue'

import ProjectService from '@/services/project.service.js'
import { useSessionStore } from '@/stores/session'

import { errorMessage, successMessage } from '@/utils/notifications'
import { usePermissions, PERMISSIONS } from '@/composables/usePermissions'

export default {
    name: 'AdminProjectsSettingsScreen',
    setup() {
        const { can } = usePermissions()
        return { can, PERMISSIONS }
    },
    components: {
        ProjectHeaderTabs,
    },
    data() {
        return {
            saving: false,
            data: {},
        }
    },
    computed: {
        sessionStore() {
            return useSessionStore()
        },
        project() {
            return this.sessionStore.data.projects.data.filter(
                (project) => project.key === this.$route.params.projectKey
            )[0]
        },
        projectChanged() {
            return JSON.stringify(this.data) !== JSON.stringify(this.project)
        },
    },
    methods: {
        reset() {
            this.data = JSON.parse(JSON.stringify(this.project))
        },
        save() {
            this.saving = true

            ProjectService.update(this.$route.params.projectKey, this.data)
                .then((data) => {
                    this.sessionStore.upsertProject(data.data)
                    successMessage('The project has been updated.')
                    this.reset()
                })
                .catch((error) => {
                    errorMessage(error)
                })
                .finally(() => {
                    this.saving = false
                })
        },
    },
    mounted() {
        this.reset()
    },
}
</script>
